import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Apply theme to <html data-theme="..."> whenever it changes.
    // Falls back to localStorage (so login/register doesn't flash) and then to "dark".
    useEffect(() => {
        const theme = user?.theme || localStorage.getItem("itotal4u_theme") || "dark";
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("itotal4u_theme", theme);
    }, [user?.theme]);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (_e) {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.session_token) {
            localStorage.setItem("itotal4u_token", data.session_token);
        }
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data?.session_token) {
            localStorage.setItem("itotal4u_token", data.session_token);
        }
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout", {});
        } catch (_e) {
            /* noop */
        }
        localStorage.removeItem("itotal4u_token");
        setUser(null);
    };

    return (
        <AuthCtx.Provider
            value={{ user, setUser, loading, login, register, logout, refresh, refreshUser: refresh, formatApiError }}
        >
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
